import { resolveUrbanZoningDocumentUrl } from '../../services/urbanZoningService';

export const ZONING_DISPLAY_GROUP_ORDER = [
  'edificabilidad',
  'principal',
  'ubicacion',
  'retiros',
  'referencia',
];

export const ZONING_DISPLAY_GROUP_LABELS = {
  edificabilidad: 'Edificabilidad',
  principal: 'Normativa',
  ubicacion: 'Ubicación',
  retiros: 'Retiros',
  referencia: 'Referencia',
};

/** Orden y títulos legacy (Córdoba Capital). Fallback hasta que el backend envíe display_rows. */
export const LEGACY_DETAIL_ROWS = [
  { id: 'denominacion', label: 'N° de Ordenanza - Perfil', source: 'canonical' },
  { id: 'zona', label: 'Zona', source: 'canonical' },
  { id: 'fos', label: 'FOS %', source: 'canonical' },
  { id: 'fot', label: 'FOT %', source: 'canonical' },
  { id: 'altura_max', label: 'Altura Máxima', source: 'canonical' },
  { id: 'retiros', label: 'Retiros', source: 'canonical' },
  { id: 'frente_min', label: 'Frente mínimo', source: 'canonical' },
  { id: 'sup_min', label: 'Superficie mínima', source: 'canonical' },
  { id: 'pisos_maximos', label: 'Máxima Cantidad de Pisos', source: 'detail' },
  { id: 'caracter_urbanistico', label: 'Carácter Urbanístico', source: 'detail' },
  { id: 'ord_original', label: 'Ordenanza Original', source: 'detail' },
  { id: 'ord_modificatoria', label: 'Ordenanza Modificatoria', source: 'detail' },
  { id: 'art_ord', label: 'Art. Ordenanza', source: 'detail' },
  { id: 'grafico', label: 'Gráfico', source: 'detail' },
  { id: 'enlace_grafico', label: 'Enlace Gráfico', source: 'detail', format: 'link' },
  { id: 'plano_a_45', label: 'Plano a 45°', source: 'detail' },
  { id: 'altura_perim', label: 'Altura Perímetro Libre', source: 'detail' },
  { id: 'pisos_lm', label: 'Máxima Cantidad de Pisos sobre Línea Municipal', source: 'detail' },
  { id: 'n_pisos_li', label: 'Máxima Cantidad de Pisos Perímetro Libre', source: 'detail' },
  { id: 'cantidad_unidades', label: 'Unidades Funcionales', source: 'detail' },
  { id: 'observacion', label: 'Observaciones', source: 'detail' },
  { id: 'informacion_adicional', label: 'Información adicional', source: 'detail' },
  { id: 'retiro_frente', label: 'Retiro Frente', source: 'detail' },
  { id: 'retiro_le', label: 'Retiro Línea Edificación', source: 'detail', format: 'link' },
  { id: 'otros_retiros', label: 'Otros Retiros', source: 'detail' },
  { id: 'otras_disposiciones', label: 'Otras Disposiciones', source: 'detail' },
  { id: 'altura_lm', label: 'Altura Línea Municipal', source: 'detail' },
];

const LEGACY_KNOWN_DETAIL_KEYS = new Set(
  LEGACY_DETAIL_ROWS.filter((row) => row.source === 'detail').map((row) => row.id),
);

const ZONING_SUMMARY_PROPERTY_KEYS = ['zonificacion_urbana', 'ocupacion_del_suelo'];
const ZONING_SUMMARY_PROPERTY_KEY_SET = new Set(ZONING_SUMMARY_PROPERTY_KEYS);

const formatPropertyLabel = (key) => (
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

export const normalizeDisplayValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const formatNumberAR = (value, decimals = 2) => new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals,
}).format(value);

const parseNumericToken = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const match = String(value).trim().match(/-?\d+(?:[.,]\d+)?/);
  if (!match) return null;

  const parsed = parseFloat(match[0].replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const appendUnit = (text, unit) => {
  const base = normalizeDisplayValue(text);
  const suffix = normalizeDisplayValue(unit);
  if (!base) return '';
  if (!suffix) return base;
  if (base.endsWith(suffix)) return base;
  return `${base} ${suffix}`;
};

/** Formatea el valor visible según `format` / `unit` del contrato display_rows. */
export const formatDisplayRowValue = (row) => {
  const format = row?.format || 'text';
  const raw = row?.value;

  if (format === 'boolean') {
    if (raw === true || raw === 'true' || raw === 1 || raw === '1') return 'Sí';
    if (raw === false || raw === 'false' || raw === 0 || raw === '0') return 'No';
    return normalizeDisplayValue(raw);
  }

  if (format === 'number') {
    const num = parseNumericToken(raw);
    if (num === null) return normalizeDisplayValue(raw);
    const unit = normalizeDisplayValue(row?.unit);
    const decimals = unit === '%' ? 2 : 2;
    return appendUnit(formatNumberAR(num, decimals), unit);
  }

  if (format === 'link') {
    return normalizeDisplayValue(raw);
  }

  const text = normalizeDisplayValue(raw);
  return appendUnit(text, row?.unit);
};

const normalizeApiDisplayRow = (row, index) => {
  if (!row || typeof row !== 'object') return null;

  const id = normalizeDisplayValue(row.id) || `display-row-${index}`;
  const label = normalizeDisplayValue(row.label);
  const value = row.value;
  const displayText = formatDisplayRowValue({
    ...row,
    id,
    label,
    value,
    format: row.format || 'text',
  });

  if (!label || !displayText) return null;

  return {
    id,
    label,
    value,
    displayText,
    group: normalizeDisplayValue(row.group) || 'principal',
    format: row.format || 'text',
    unit: row.unit ?? null,
    highlight: Boolean(row.highlight),
  };
};

/** Filas listas para render desde `display_rows` del backend. null si no hay filas válidas. */
export const normalizeDisplayRowsFromApi = (zoningData) => {
  const rows = zoningData?.display_rows;
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const normalized = rows
    .map((row, index) => normalizeApiDisplayRow(row, index))
    .filter(Boolean);

  return normalized.length > 0 ? normalized : null;
};

export const pickZoningSummaryText = (properties) => {
  const props = properties && typeof properties === 'object' ? properties : {};
  for (const key of ZONING_SUMMARY_PROPERTY_KEYS) {
    const value = normalizeDisplayValue(props[key]);
    if (value) return value;
  }
  return '';
};

const buildLegacyRow = (row, value) => ({
  id: row.id,
  label: row.label,
  value,
  displayText: formatDisplayRowValue({
    id: row.id,
    label: row.label,
    value,
    format: row.format || 'text',
    unit: row.unit ?? null,
  }),
  group: 'principal',
  format: row.format || 'text',
  unit: row.unit ?? null,
  highlight: false,
});

/** Fallback: canonical/detail hasta migración completa a display_rows. Omite filas vacías. */
export const buildLegacyDisplayRows = (
  zoningData,
  { hiddenIds = new Set(), includeExtraProperties = true } = {},
) => {
  const properties = zoningData?.detail && typeof zoningData.detail === 'object'
    ? zoningData.detail
    : {};
  const canonical = zoningData?.canonical && typeof zoningData.canonical === 'object'
    ? zoningData.canonical
    : {};

  const rows = [];

  const summaryText = pickZoningSummaryText(properties);
  if (summaryText && !hiddenIds.has('zonificacion_urbana')) {
    rows.push({
      id: 'zonificacion_urbana',
      label: 'Zonificación Urbana',
      value: summaryText,
      displayText: summaryText,
      group: 'principal',
      format: 'text',
      unit: null,
      highlight: false,
    });
  }

  LEGACY_DETAIL_ROWS.forEach((row) => {
    if (hiddenIds.has(row.id)) return;

    const rawValue = row.source === 'canonical' ? canonical[row.id] : properties[row.id];
    const displayText = formatDisplayRowValue({
      id: row.id,
      label: row.label,
      value: rawValue,
      format: row.format || 'text',
    });
    if (!displayText) return;

    rows.push(buildLegacyRow(row, rawValue));
  });

  if (includeExtraProperties) {
    Object.entries(properties)
      .filter(([key]) => !LEGACY_KNOWN_DETAIL_KEYS.has(key) && !ZONING_SUMMARY_PROPERTY_KEY_SET.has(key))
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([key, value]) => {
        const displayText = formatDisplayRowValue({
          id: key,
          label: formatPropertyLabel(key),
          value,
          format: 'text',
        });
        if (!displayText) return;

        rows.push({
          id: key,
          label: formatPropertyLabel(key),
          value,
          displayText,
          group: 'referencia',
          format: 'text',
          unit: null,
          highlight: false,
        });
      });
  }

  return rows;
};

/** Prioriza display_rows del backend; si no hay, usa canonical/detail legacy. */
export const resolveZoningDisplayRows = (
  zoningData,
  { hiddenIds = new Set(), includeExtraProperties = true } = {},
) => {
  const fromApi = normalizeDisplayRowsFromApi(zoningData);
  if (fromApi) {
    return { mode: 'display_rows', rows: fromApi };
  }

  return {
    mode: 'legacy',
    rows: buildLegacyDisplayRows(zoningData, { hiddenIds, includeExtraProperties }),
  };
};

/** Une filas de edificabilidad (lote) con filas normativas (`display_rows` o legacy). */
export const mergeEdificabilidadAndZoningRows = (edificabilidadRows = [], zoningRows = []) => [
  ...(Array.isArray(edificabilidadRows) ? edificabilidadRows : []),
  ...(Array.isArray(zoningRows) ? zoningRows : []),
];

/** Agrupa filas consecutivas por `group`, respetando el orden del backend. */
export const groupDisplayRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const groups = [];
  let current = null;

  rows.forEach((row) => {
    const groupId = row.group || 'principal';
    if (!current || current.id !== groupId) {
      current = { id: groupId, rows: [] };
      groups.push(current);
    }
    current.rows.push(row);
  });

  return groups;
};

export const resolveDisplayRowLinkHref = (row, linkContext) => {
  if (row?.format !== 'link') return null;

  const raw = normalizeDisplayValue(row.value);
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  return resolveUrbanZoningDocumentUrl(row.id, raw, linkContext);
};
